import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { Button, CardContent, Grid, Typography } from "@material-ui/core";
import QuestionComponent from "./QuestionComponent";
import { useTranslation } from "react-i18next";
import { get, sequence } from "@totalsoft/change-tracking-react";
import * as V from "@totalsoft/pure-validations";
import { useValidation } from "@totalsoft/pure-validations-react";
import { isValidAnswer, isMandatoryField } from "utils/simple-validations/validations";
import { isDirty } from "@totalsoft/change-tracking";
import { SAVE_SCORING_QUESTIONS_WITH_ANSWERS, REQUEST_APPROVAL } from "features/quotes/mutations";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { addToast } from "components/notifications";
import { useParams } from "react-router";
import { DonutLarge, Check } from "@material-ui/icons";
import moment from "moment";
import { useApolloClient, gql } from "@apollo/client";
import { invalidateApolloClientCache } from "utils/functions";

const getValidator = () => {
  return V.shape({
    questions: V.items(
      V.fromModel((question) =>
        V.shape({
          questionAnswer: [isMandatoryField(question), isValidAnswer(question)] |> V.stopOnFirstFailure,
        })
      )
    ),
  });
};

function QuestionsContainer(props) {
  const {
    loading,
    questionsLens,
    allowAutomatedDecision,
    questionsLensDirtyInfo,
    resetQuestionsLens,
    disabled,
  } = props;
  const { t } = useTranslation();
  const { quoteId } = useParams();
  const client = useApolloClient();
  const questionsAndAnswersList = questionsLens.questions |> sequence;
  const validator = useMemo(() => getValidator(), []);
  const [validation, validate] = useValidation(validator);

  const [saveScoringQuestionsAnswers] = useMutationWithErrorHandling(SAVE_SCORING_QUESTIONS_WITH_ANSWERS, {
    onCompleted: (data) => {
      if (!data) return;
      addToast(t("ToastNotifications.Success"), "success");
      const questions = questionsLens.questions |> get;
      resetQuestionsLens({ questions });

      questions.map((q) => {
        client.cache.writeFragment({
          id: client.cache.identify({
            __typename: "ScoringQuestion",
            quoteId: parseInt(quoteId),
            questionId: q.questionId,
          }),
          fragment: gql`
            fragment ScoringQuestion on ScoringQuestion {
              questionAnswer
            }
          `,
          data: {
            questionAnswer: q.questionAnswer,
          },
        });
      });
    },
  });

  const handleSave = useCallback(() => {
    const model = questionsLens |> get;
    if (!validate(model)) return;

    saveScoringQuestionsAnswers({
      variables: {
        input: {
          quoteId: parseInt(quoteId),
          scoringQuestions: model.questions.map((x) => ({
            questionId: x.questionId,
            questionAnswer:
              !x?.questionAnswer && x?.questionAnswer != 0
                ? null
                : x.questionAnswer instanceof Date
                ? moment(x.questionAnswer).format("YYYY-MM-DD")
                : x.questionAnswer.toString(),
            questionCode: x.questionCode,
            isMandatory: x.isMandatory,
          })),
        },
      },
    });
  }, [questionsLens, validate, quoteId, saveScoringQuestionsAnswers]);

  const [requestApproval] = useMutationWithErrorHandling(REQUEST_APPROVAL);

  const handleInitiateAutomatedDecision = useCallback(async () => {
    const { data } = await requestApproval({ variables: { quoteId: parseInt(quoteId), indicative: true } });
    if (!data) return;
    addToast(t("ToastNotifications.Success"), "success");
    invalidateApolloClientCache(client, { __typename: "Quote", quoteId: parseInt(quoteId) }, [
      "zootDocDecisionFinished",
      "zootDocDecisionColor",
      "zootDocDecisionReasons",
    ]);
  }, [quoteId, requestApproval, client, t]);

  const canRequestIndicativeDecision = useMemo(() => {
    if (!allowAutomatedDecision) return false;
    if (!questionsAndAnswersList) return;
    if (isDirty(questionsLensDirtyInfo)) return false;
    if (questionsAndAnswersList.some((item) => (item.isMandatory |> get) && (item.questionAnswer |> get) === null))
      return false;
    return true;
  }, [questionsAndAnswersList, questionsLensDirtyInfo, allowAutomatedDecision]);

  const showQuestions = !loading && Boolean(questionsAndAnswersList?.length);

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignContent="center" direction="row">
          <Grid item xs={12}>
            <Typography variant="h4">{t("DataCompletion.QuestionsAndAnswers.InitiateGetDecision")}</Typography>
          </Grid>
          {loading && <LoadingFakeText lines={5} />}
          {showQuestions && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">{t("DataCompletion.QuestionsAndAnswers.QuestionsTitle")}</Typography>
              </Grid>
              <Grid item xs={12}>
                {questionsAndAnswersList.map((item, index) => (
                  <QuestionComponent
                    key={index}
                    questionAndAnswer={item}
                    validation={validation.questions[index]}
                    disabled={disabled}
                  />
                ))}
              </Grid>
            </>
          )}
          <Grid item container xs={12} spacing={1} justifyContent="flex-end">
            {allowAutomatedDecision && (
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!canRequestIndicativeDecision || disabled}
                  onClick={handleInitiateAutomatedDecision}
                  endIcon={<DonutLarge />}
                >
                  {t("DataCompletion.QuestionsAndAnswers.InitiateGetDecision")}
                </Button>
              </Grid>
            )}
            {showQuestions && (
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={!isDirty(questionsLensDirtyInfo) || disabled}
                  endIcon={<Check />}
                >
                  {t("General.Save")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

QuestionsContainer.propTypes = {
  loading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  allowAutomatedDecision: PropTypes.bool.isRequired,
  questionsLens: PropTypes.object.isRequired,
  questionsLensDirtyInfo: PropTypes.object.isRequired,
  resetQuestionsLens: PropTypes.func.isRequired,
};

export default QuestionsContainer;
